import axios from "axios";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";

// (2026-10) Nota de remisión de un surtido. La usan ListadoVentas (botón PDF
// del renglón) y ListadoPendienteSurtir (sale sola al guardar un surtido),
// para que las dos impriman exactamente la misma nota.

// Dirección de entrega con los datos del cliente; las partes sin dato vienen
// como "NA" o "0" y no se imprimen.
export const direccionDeCliente = (c) =>
  [
    c?.calle,
    c?.numero_ext ? `No. ${c.numero_ext}` : "",
    c?.numero_int ? `Int. ${c.numero_int}` : "",
    c?.colonia ? `Col. ${c.colonia}` : "",
    c?.delegacion,
    c?.ciudad,
    c?.estado,
    c?.cp ? `C.P. ${c.cp}` : "",
  ]
    .map((t) => String(t || "").trim())
    .filter((t) => t && !/(^|\s)(NA|0)$/i.test(t))
    .join(", ");

// Arma los datos de la nota a partir del surtido como lo regresa el back
// (GET /surtidos/:id, con user, empresas, clientes y pedidos poblados).
export const datosNotaDesdeSurtido = (a) => [
  a.fecha,
  a.clientes?.[0]?._id,
  a.clientes?.[0]?.nombre_comercial || "",
  a.observaciones,
  a.idSurtido,
  a._id,
  `${a.user?.[0]?.nombre || ""} ${a.user?.[0]?.apellido || ""}`.trim(),
  a.pedidos?.[0]?.pedidoCliente,
  a.impuestos,
  a.subTotal,
  a.iva,
  a.total_general,
  a.totalCantidad,
  a.clientes?.[0]?.plazo,
  a.numeroFactura,
  a.descuento,
  a.descuento2,
  {
    empresa: a.empresas?.[0]?.name || "",
    subCliente: a.pedidos?.[0]?.subCliente || "NA",
    telefono: a.clientes?.[0]?.telefono || "",
    direccion: direccionDeCliente(a.clientes?.[0]),
    esVentaDirecta: a.esVentaDirecta || a.pedidos?.[0]?.esVentaDirecta || "No",
  },
];

// Trae el surtido recién guardado y saca su nota.
export async function notaRemisionPorId(idSurtido) {
  const { data } = await axios.get(
    `${process.env.REACT_APP_URL_SURTIDOS}/${idSurtido}`,
    {
      headers: {
        Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
      },
    }
  );
  return PDFNotaRemision(...datosNotaDesdeSurtido(data));
}

// Notas de remisión (surtidos activos) que han salido de un pedido, cada una
// con sus renglones, ordenadas por folio. Las usa el PDF de pendiente por
// surtir para anexarlas.
export async function notasRemisionDePedido(idPedido) {
  const headers = {
    Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
  };
  const { data: renglones } = await axios.get(
    `${process.env.REACT_APP_URL_ARTICULOS_SURTIDOS}/pedido/${idPedido}`,
    { headers }
  );
  const ids = [
    ...new Set(
      renglones.map((r) => String(r.surtidos?.[0]?._id || "")).filter((id) => id)
    ),
  ];
  const notas = await Promise.all(
    ids.map(async (id) => {
      const [{ data: surtido }, { data: articulos }] = await Promise.all([
        axios.get(`${process.env.REACT_APP_URL_SURTIDOS}/${id}`, { headers }),
        axios.get(`${process.env.REACT_APP_URL_ARTICULOS_SURTIDOS}/surtido/${id}`, {
          headers,
        }),
      ]);
      return { surtido, articulos };
    })
  );
  return notas
    .filter((n) => n.surtido && n.surtido.is_active === "Si")
    .sort((x, y) => Number(x.surtido.idSurtido || 0) - Number(y.surtido.idSurtido || 0));
}

// Piezas y totales de la nota (lo mismo que imprime su caja de totales).
export const totalesNota = (articulos = [], descuento, descuento2) => {
  let piezas = 0;
  let subtotal = 0;
  articulos.forEach((a) => {
    const cantidad = Number(a.cantidad || 0);
    if (cantidad <= 0) return;
    piezas += cantidad;
    subtotal += cantidad * Number(a.precio || 0);
  });
  const d1 = (subtotal * Number(descuento || 0)) / 100;
  const d2 = (subtotal * Number(descuento2 || 0)) / 100;
  const iva = (subtotal - d1 - d2) * 0.16;
  return { piezas, subtotal, d1, d2, iva, total: subtotal - d1 - d2 + iva };
};

// Datos de la nota en forma de objeto, para dibujarla dentro de otro PDF.
export const notaDesdeSurtido = (surtido, articulos) => {
  const [
    fecha,
    ,
    clientes,
    observaciones,
    numero,
    ,
    user,
    ,
    ,
    ,
    ,
    ,
    ,
    ,
    numeroFactura,
    descuento,
    descuento2,
    extra,
  ] = datosNotaDesdeSurtido(surtido);
  return {
    fecha,
    clientes,
    observaciones,
    numero,
    user,
    numeroFactura,
    descuento,
    descuento2,
    extra,
    articulos,
  };
};

// (2026-10) Nota de remisión con el formato que pidió el cliente (ejemplo
// "Nota de Remision - Folio 126"): encabezado con empresa y folio, cuadro de
// datos, un renglón por artículo con sus tallas en texto, observaciones con
// totales, firmas y pie de página con el folio en todas las hojas.
export async function PDFNotaRemision(
  fecha,
  idClientes,
  clientes,
  observaciones,
  numero,
  idPDFOC,
  user,
  pedidoCliente,
  impuestos,
  subTotal,
  iva,
  total_general,
  totalCantidad,
  plazo,
  numeroFactura,
  descuento,
  descuento2,
  extra = {}
) {
  try {
    const { data: allArticulosSurtido } = await axios.get(
      `${process.env.REACT_APP_URL_ARTICULOS_SURTIDOS}/surtido/${idPDFOC}`,
      {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      }
    );

    // TK6 — preguntar si la nota sale con precio
    const promptPrecioOC = await Swal.fire({
      title: "¿La nota sale con precio?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí",
      cancelButtonText: "No",
    });

    const doc = new jsPDF();
    dibujaNotaRemision(doc, {
      fecha,
      clientes,
      observaciones,
      numero,
      user,
      numeroFactura,
      descuento,
      descuento2,
      extra,
      articulos: allArticulosSurtido,
      mostrarPrecio: promptPrecioOC.isConfirmed,
    });
    doc.save(`Nota de Remision - Folio ${numero}.pdf`);
  } catch (err) {
    console.error("Error generando PDFOC:", err);
    Swal.fire("Error", "No se pudo generar el PDF", "error");
  }
}

// Dibuja la nota en `doc` empezando en la hoja actual (que debe venir vacía:
// el que la anexa a otro PDF hace antes doc.addPage()). El pie cuenta las
// páginas de la nota, no las del documento completo.
export function dibujaNotaRemision(
  doc,
  {
    fecha,
    clientes,
    observaciones,
    numero,
    user,
    numeroFactura,
    descuento,
    descuento2,
    extra = {},
    articulos: allArticulosSurtido = [],
    mostrarPrecio,
  }
) {
  const paginaInicio = doc.internal.getNumberOfPages();
  const txt = (v) => String(v ?? "").trim();
  // "NA", "No", "0" y vacío se tratan como sin dato
  const dato = (v) => {
    const t = txt(v);
    return !t || ["NA", "NO", "0"].includes(t.toUpperCase()) ? "" : t;
  };
  const moneda = (n) =>
    "$" +
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(n || 0));
  const fechaCorta = (f) => {
    const [a, m, d] = txt(f).slice(0, 10).split("-");
    return a && m && d ? `${d}/${m}/${a}` : txt(f);
  };

  const ordenTallas = ["2XCH","XXCH","XCH","EXTRA CHICA","CHICA","CH","M","MEDIANA","GRANDE","G","EXTRA GRANDE","XG","2XG","XXG","3XG","XXXG","4XG","XXXXG","5XG","XXXXXG","6XG","ESP","VARIOS","UNITALLA","1","2","3","4","4-6","5","6","7","8","8-10","9","10","10-12","11","12","13","14","14-16","15","16","17","18","19","20","21","22","23","24","25","26","27","28","29","30","31","32","33","34","35","36","37","38","39","40","41","42","43","44","45","46","47","48","49","50","51","52","53","54","55","56","57","58","59","60","61","62"];
  const posTalla = (t) => {
    const i = ordenTallas.indexOf(t);
    return i === -1 ? Infinity : i;
  };

  // ---- Un renglón por artículo + color + precio, con sus tallas en texto
  const renglones = [];
  const porClave = {};
  allArticulosSurtido.forEach((a) => {
    const cantidad = Number(a.cantidad || 0);
    if (cantidad <= 0) return;
    const art = a.articulos?.[0] || {};
    // L118 (2026-06) — subDepartamento del artículo en vez de la descripción
    const prenda =
      txt(art.subDepartamentos?.[0]?.name) || txt(art.descripcion);
    const color = txt(a.colores?.[0]?.name);
    const precio = Number(a.precio || 0);
    const clave = `${art._id || art.codigo}||${color}||${precio.toFixed(2)}`;
    if (!porClave[clave]) {
      porClave[clave] = {
        descripcion: [txt(art.codigo), prenda].filter((t) => t).join(" ") +
          (color ? ` · ${color}` : ""),
        precio,
        cantidad: 0,
        tallas: new Map(),
      };
      renglones.push(porClave[clave]);
    }
    const r = porClave[clave];
    const talla = txt(a.tallas?.[0]?.name) || "S/T";
    const variante = txt(a.tallas?.[0]?.variantesTallas?.[0]?.name);
    const etiqueta = variante ? `${talla} (${variante})` : talla;
    r.cantidad += cantidad;
    r.tallas.set(etiqueta, {
      talla,
      cantidad: (r.tallas.get(etiqueta)?.cantidad || 0) + cantidad,
    });
  });

  const textoTallas = (r) =>
    [...r.tallas.entries()]
      .sort((x, y) => posTalla(x[1].talla) - posTalla(y[1].talla))
      .map(([etiqueta, v]) => `${etiqueta}/${v.cantidad}`)
      .join(" ");

  const {
    piezas,
    subtotal: subtotalGlobal,
    d1,
    d2,
    iva: ivaFinal,
    total: totalFinal,
  } = totalesNota(allArticulosSurtido, descuento, descuento2);

  // ---- Estilo
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const ML = 17;
  const anchoUtil = pageW - ML * 2;
  const azul = [30, 58, 138];
  const azulClaro = [37, 99, 235];
  const fondoEtiqueta = [238, 244, 255];
  const borde = [169, 180, 200];
  const rojo = [220, 38, 38];

  const empresa =
    txt(extra.empresa) || txt(process.env.REACT_APP_NOMBRE_INSTITUCION);
  const cliente =
    txt(clientes) +
    (dato(extra.subCliente) ? ` (${txt(extra.subCliente)})` : "");
  const esVD = txt(extra.esVentaDirecta).toLowerCase() === "si";
  const factura = dato(numeroFactura);

  // ---- Encabezado
  doc.setFont(undefined, "bold").setFontSize(17).setTextColor(...azul);
  doc.text(empresa.toUpperCase(), ML, 20);
  doc.setFontSize(11.5).setTextColor(...azulClaro);
  doc.text("NOTA DE REMISIÓN", ML, 26.5);
  doc.setFontSize(10.5).setTextColor(...azul);
  doc.text("FOLIO", pageW - ML, 19, { align: "right" });
  doc.setFontSize(16).setTextColor(...rojo);
  doc.text(String(numero), pageW - ML, 26.5, { align: "right" });
  doc.setTextColor(0).setFont(undefined, "normal");

  // ---- Cuadro de datos
  const etiqueta = (content, extraStyles = {}) => ({
    content,
    styles: {
      fillColor: fondoEtiqueta,
      textColor: azul,
      fontStyle: "bold",
      ...extraStyles,
    },
  });
  doc.autoTable({
    startY: 30,
    margin: { left: ML, right: ML },
    tableWidth: anchoUtil,
    theme: "grid",
    styles: {
      fontSize: 9.5,
      cellPadding: 2,
      valign: "middle",
      lineColor: borde,
      lineWidth: 0.25,
      textColor: [30, 30, 30],
    },
    columnStyles: {
      0: { cellWidth: 30 },
      1: { cellWidth: 53 },
      2: { cellWidth: 37 },
    },
    body: [
      [etiqueta("Empresa:"), empresa, etiqueta("Fecha:"), fechaCorta(fecha)],
      [
        etiqueta("Tipo de salida:"),
        esVD ? "Venta Directa" : "Entrega",
        etiqueta("Respaldo de Salida:"),
        factura ? "Factura" : "Nota de Remisión",
      ],
      [etiqueta("Responsable:"), txt(user), etiqueta("# Factura:"), factura],
      [
        etiqueta("Cliente:"),
        cliente,
        etiqueta("Teléfono contacto:"),
        dato(extra.telefono),
      ],
      [
        etiqueta("Dirección de Entrega:", { minCellHeight: 13 }),
        { content: txt(extra.direccion), colSpan: 3 },
      ],
    ],
  });

  // ---- Artículos
  const head = mostrarPrecio
    ? [["CANTIDAD", "DESCRIPCIÓN", "TALLAS", "PRECIO", "TOTAL"]]
    : [["CANTIDAD", "DESCRIPCIÓN", "TALLAS"]];
  const body = renglones.map((r) => {
    const fila = [String(r.cantidad), r.descripcion, textoTallas(r)];
    if (mostrarPrecio) fila.push(moneda(r.precio), moneda(r.cantidad * r.precio));
    return fila;
  });
  doc.autoTable({
    startY: doc.autoTable.previous.finalY + 5,
    margin: { left: ML, right: ML, bottom: 22 },
    tableWidth: anchoUtil,
    theme: "grid",
    head,
    body,
    styles: {
      fontSize: 9.5,
      cellPadding: 2,
      valign: "middle",
      overflow: "linebreak",
      lineColor: borde,
      lineWidth: 0.25,
      textColor: [30, 30, 30],
    },
    headStyles: {
      fillColor: azul,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
      fontSize: 9,
    },
    columnStyles: mostrarPrecio
      ? {
          0: { cellWidth: 21, halign: "center" },
          1: { cellWidth: 45 },
          2: { cellWidth: 64 },
          3: { cellWidth: 21, halign: "right" },
          4: { halign: "right" },
        }
      : {
          0: { cellWidth: 21, halign: "center" },
          1: { cellWidth: 62 },
        },
  });

  // ---- Observaciones + totales
  const filasTot = mostrarPrecio
    ? [
        ["SUB TOTAL", moneda(subtotalGlobal)],
        ...(Number(descuento) > 0
          ? [[`DESCUENTO ${descuento}%`, `-${moneda(d1)}`]]
          : []),
        ...(Number(descuento2) > 0
          ? [[`DESCUENTO 2 ${descuento2}%`, `-${moneda(d2)}`]]
          : []),
        ["IVA", moneda(ivaFinal)],
        ["TOTAL", moneda(totalFinal)],
      ]
    : [["PIEZAS", String(piezas)]];
  const anchoTot = 57;
  const xTot = pageW - ML - anchoTot;
  const obsLineas = doc
    .setFontSize(9.5)
    .splitTextToSize(dato(observaciones), xTot - ML - 8);
  const altoObs = 13 + obsLineas.length * 4.5;
  // los renglones de totales se estiran para llenar el alto de la caja
  const altoMin = Math.max(altoObs, 28);
  const altoFilaTot = Math.max(8.5, altoMin / filasTot.length);
  const altoCajaEstimado = Math.max(altoMin, altoFilaTot * filasTot.length);

  let y = doc.autoTable.previous.finalY + 4;
  // la caja y las firmas van juntas; si no caben se pasan a otra hoja
  if (y + altoCajaEstimado + 42 > pageH - 22) {
    doc.addPage();
    y = 20;
  }

  doc.autoTable({
    startY: y,
    margin: { left: xTot, right: ML },
    tableWidth: anchoTot,
    theme: "grid",
    body: filasTot.map(([lbl, val], i) => {
      const esTotal = mostrarPrecio && i === filasTot.length - 1;
      return [
        etiqueta(lbl),
        {
          content: val,
          styles: {
            halign: "right",
            fontStyle: esTotal ? "bold" : "normal",
            textColor: esTotal ? azul : [30, 30, 30],
          },
        },
      ];
    }),
    styles: {
      fontSize: 10,
      cellPadding: 2.3,
      valign: "middle",
      lineColor: borde,
      lineWidth: 0.25,
      minCellHeight: altoFilaTot,
    },
    columnStyles: { 0: { cellWidth: 26 } },
  });
  const altoCaja = Math.max(doc.autoTable.previous.finalY - y, altoObs);

  doc.setDrawColor(...borde).setLineWidth(0.25);
  doc.rect(ML, y, anchoUtil, altoCaja);
  doc.line(xTot, y, xTot, y + altoCaja);
  doc.setFont(undefined, "bold").setFontSize(10).setTextColor(...azul);
  doc.text("Observaciones generales:", ML + 3, y + 6.5);
  doc.setFont(undefined, "normal").setFontSize(9.5).setTextColor(30, 30, 30);
  if (obsLineas.length) doc.text(obsLineas, ML + 3, y + 12.5);

  // ---- Firmas
  const yFirma = y + altoCaja + 34;
  const anchoFirma = 68;
  const xFirma1 = ML + 8;
  const xFirma2 = pageW - ML - 8 - anchoFirma;
  doc.setDrawColor(...azul).setLineWidth(0.35);
  doc.line(xFirma1, yFirma, xFirma1 + anchoFirma, yFirma);
  doc.line(xFirma2, yFirma, xFirma2 + anchoFirma, yFirma);
  doc.setFont(undefined, "bold").setFontSize(10).setTextColor(...azul);
  doc.text("Entregó (Nombre y firma)", xFirma1 + anchoFirma / 2, yFirma + 5.5, {
    align: "center",
  });
  doc.text("Recibió (Nombre y firma)", xFirma2 + anchoFirma / 2, yFirma + 5.5, {
    align: "center",
  });

  // ---- Pie de página en todas las hojas de la nota
  const paginaFin = doc.internal.getNumberOfPages();
  const nPaginas = paginaFin - paginaInicio + 1;
  for (let i = paginaInicio; i <= paginaFin; i++) {
    doc.setPage(i);
    doc.setFont(undefined, "normal").setFontSize(8.5).setTextColor(60, 60, 60);
    doc.text(`${empresa.toUpperCase()} - Nota de Remisión`, ML, pageH - 12);
    doc.text(`Folio: ${numero}`, pageW / 2, pageH - 12, { align: "center" });
    doc.text(`Página ${i - paginaInicio + 1} de ${nPaginas}`, pageW - ML, pageH - 12, {
      align: "right",
    });
  }
  doc.setPage(paginaFin);
  doc.setTextColor(0);
}
